/**
 * Step 2 — what it means for them (downsizing rebuild, 21 Oct 2026; the agreed mock-up's j1-step2).
 *
 * Staying put in four numbers — every plan on step 3 is measured against them — with "why 10?" beside the
 * horizon (owner, 19 Oct 2026: 10 years in every module but aged care, said on the screen). Then what the
 * sale would bring in, and the trap in their own figures: what Centrelink counts today, which test sets the
 * pension, and how much freed money would stop it.
 *
 * ONE SOURCE: the pension and cashflow are today's assessment (`todayFinancials`), the estate and the runway
 * the stay-put projection — the same run the engine's own baseline makes.
 */
import React from 'react';
import { ArrowRight, ChevronLeft } from 'lucide-react';
import { householdVoice } from '../../utils/householdWords.ts';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { FundingRunway } from '../../components/narrative/FundingRunway.tsx';
import type { PlanRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { RATES_PROVENANCE, ANNUAL_REDUCTION_PER_1000_ASSETS } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { PlainWordsCard } from '../../components/narrative/PlainWords.tsx';
import { MeansTestBeats, readMeansTestPosition, type MeansTestBreakdownRow } from '../../components/insights/MeansTestPosition.tsx';

const money = (n: number) => formatCurrency(Math.round(n));

/** Why downsizing plans over 10 years — the one explainer, on step 2, the questions and the plans. */
export const DZ_WHY_TEN = 'Ten years is long enough for what a move costs on the day — selling costs and stamp duty — to be weighed against what it changes every year: the Age Pension, what is left over, and how the new home and the money grow. It is short enough to be a fair projection. The village and Support at home plan over the same 10 years; only aged care uses age and health instead.';

/**
 * The trap in the client's own figures: what is counted, which test sets the pension, and about how much
 * freed money would stop it. Null when the engine's breakdown is not there (below pension age, no pension).
 */
export function dzTrapLine(todayFinancials: { socialSecurity?: number; socialSecurityBreakdown?: unknown } | null | undefined, isCouple: boolean, they: string): string | null {
    const pos = readMeansTestPosition(todayFinancials?.socialSecurityBreakdown as MeansTestBreakdownRow[] | undefined, todayFinancials?.socialSecurity, isCouple);
    if (!pos) return null;
    const pension = Number(todayFinancials?.socialSecurity) || 0;
    const counted = `Today Centrelink counts ${money(pos.assessableAssets)} of ${they === 'you' ? 'your' : 'their'} assets`;
    if (!(pension >= 1)) return `${counted}, and no Age Pension is paid, so the money the sale frees cannot reduce it.`;
    if (pos.determinedBy === 'income') return `${counted}, and the pension is set by the income test: the money the sale frees is deemed to earn income, so it lowers the pension as that income rises.`;
    // Assets test: the pension falls by $78 a year for every $1,000 more counted ($3 a fortnight).
    const stops = Math.ceil((pos.assetTestPension / ANNUAL_REDUCTION_PER_1000_ASSETS) * 1000 / 10_000) * 10_000;
    return `${counted}, and the pension is set by the assets test, so any money the sale frees reduces the pension, and freeing about ${money(stops)} stops it.`;
}

interface DzStep2Props {
    factFind: DraftFactFind;
    todayFinancials: { socialSecurity?: number; cashFlow?: number; netWorth?: number; socialSecurityBreakdown?: unknown } | null;
    stayPutLegacy: number | null;
    runway: PlanRunwayCopy | null;
    legacyYears: number;
    salePrice: number;
    sellingCosts: number;
    onLegacyYearsChange?: (years: number) => void;
    onBack: () => void;
    onNext: () => void;
    onAssumptions: () => void;
    onShowProjections: () => void;
}

const card = 'rounded-2xl border border-slate-200 bg-white p-6 shadow-card';

const Tile = ({ label, value, unit, caption, tone }: { label: string; value: string; unit?: string; caption: React.ReactNode; tone?: 'good' | 'bad' }) => (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-card">
        <p className="text-[12.5px] font-semibold text-slate-500">{label}</p>
        <p className={`mt-1 text-[26px] font-bold leading-none tabular-nums tracking-tight ${tone === 'good' ? 'text-emerald-700' : tone === 'bad' ? 'text-rose-700' : 'text-slate-900'}`}>
            {value}{unit && <span className="ml-1 text-[13px] font-semibold text-slate-500">{unit}</span>}
        </p>
        <div className="mt-2 text-[12.5px] leading-snug text-slate-500">{caption}</div>
    </div>
);

const DzStep2 = ({ factFind, todayFinancials, stayPutLegacy, runway, legacyYears, salePrice, sellingCosts, onLegacyYearsChange, onBack, onNext, onAssumptions, onShowProjections }: DzStep2Props) => {
    const v = householdVoice(factFind);
    const ready = !!todayFinancials;
    const pension = todayFinancials?.socialSecurity ?? 0;
    const cashFlow = todayFinancials?.cashFlow ?? 0;
    const netWorth = todayFinancials?.netWorth ?? 0;
    const years = Number(legacyYears) || 0;
    const [showWhy, setShowWhy] = React.useState(false);
    const [showTen, setShowTen] = React.useState(false);
    const trap = dzTrapLine(todayFinancials, factFind?.adviceFor === 'couple', v.they);
    const net = Math.max(0, salePrice - sellingCosts);
    const whyTen = <button type="button" onClick={() => setShowTen((o) => !o)} aria-expanded={showTen} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2" data-testid="dz-why-ten">why {years}?</button>;

    return (
        <div className="space-y-5" data-testid="dz-step2">
            <div>
                <h2 className="text-[24px] font-bold tracking-tight text-brand-navy">If {v.names} stay{v.s} where {v.they} are</h2>
                <p className="mt-1 text-[15px] text-slate-600">Every way of downsizing on the next step is measured against these four numbers, which the cards call &ldquo;staying put&rdquo;.</p>
            </div>
            {!ready ? (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-[14px] text-slate-600">Working out today&apos;s position…</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="dz-four-numbers">
                        <Tile label="Age Pension" value={money(pension)} unit="a year" caption={<>{v.couple ? 'the two of them' : 'a year'} · <button type="button" onClick={() => setShowWhy((o) => !o)} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2">why?</button></>} />
                        <Tile label={cashFlow < 0 ? 'Short each year' : 'Left over each year'} value={money(Math.abs(cashFlow))} tone={cashFlow < 0 ? 'bad' : 'good'} caption={cashFlow < 0 ? 'money in, less money out; met from savings' : 'money in, less money out'} />
                        <Tile label={`What ${v.they} own`} value={money(netWorth)} caption={`less what ${v.they} owe`} />
                        <Tile label="Left to the family" value={stayPutLegacy != null ? money(stayPutLegacy) : '—'} caption={stayPutLegacy != null ? (onLegacyYearsChange ? <span className="inline-flex flex-wrap items-center gap-1"><ProjectionYearsInput years={years} onChange={onLegacyYearsChange} before="after about" after=" · " /> {whyTen}</span> : <>after about {years} years · {whyTen}</>) : 'not available: the projection could not be run'} />
                    </div>
                    {showTen && <p className="rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-white px-3 py-2 text-[13.5px] leading-relaxed text-slate-700" data-testid="dz-why-ten-text"><strong>Why {years} years?</strong> {DZ_WHY_TEN}</p>}
                    <p className="text-[14px]">
                        <button type="button" onClick={onShowProjections} className="font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4" data-testid="dz-step2-year-by-year">See the year-by-year figures, staying put</button>
                        <span className="text-slate-500"> — Age Pension, money in and out, and what {v.they} own, each year for about {years} years.</span>
                    </p>
                    {showWhy && (
                        <div className={card}>
                            <PlainWordsCard eyebrow={`Why ${v.possessive} Age Pension is ${money(pension)}`} className="-m-5 border-0 bg-transparent shadow-none">
                                <MeansTestBeats careDepositRelevant={false} financials={todayFinancials} isCouple={factFind?.adviceFor === 'couple'} name={v.names} alsoMoves={<>changes what downsizing does to the pension</>} />
                            </PlainWordsCard>
                        </div>
                    )}
                </>
            )}

            {salePrice > 0 && (
                <div className={card} data-testid="dz-sale-brings">
                    <p className="text-[12px] font-semibold text-brand-navy">What the sale would bring in</p>
                    <table className="mt-1 w-full max-w-xl text-[14px] tabular-nums">
                        <tbody>
                            <tr className="border-b border-slate-100"><td className="py-2 text-slate-700">Sale price</td><td className="py-2 text-right text-slate-900">{money(salePrice)}</td></tr>
                            <tr className="border-b border-slate-100"><td className="py-2 text-slate-700">Selling costs</td><td className="py-2 text-right text-rose-700">−{money(sellingCosts)}</td></tr>
                            <tr><td className="py-2 font-semibold text-slate-900">Net proceeds</td><td className="py-2 text-right font-bold text-slate-900" data-testid="dz-net-proceeds">{money(net)}</td></tr>
                        </tbody>
                    </table>
                    <p className="mt-3 text-[15px] leading-relaxed text-slate-700" data-testid="dz-trap-line">
                        <strong className="text-slate-900">Every dollar of that which doesn&apos;t go into the new home is counted for the Age Pension.</strong>{trap ? ` ${trap}` : ''}
                    </p>
                    {runway && (
                        <>
                            <p className="mt-5 text-[12px] font-semibold text-brand-navy">How long the money lasts, staying put</p>
                            <div className="mt-1">
                                <FundingRunway headline={<span className="text-[15px] text-slate-700">{runway.headline}</span>} coveredPct={runway.coveredPct} leftLabel={runway.leftLabel} rightLabel={runway.rightLabel} depleted={runway.depleted} flag={runway.flag} />
                            </div>
                        </>
                    )}
                </div>
            )}

            <p className="text-[13px] text-slate-500">
                Figures use the rates in force from {RATES_PROVENANCE.agePension}. <button type="button" onClick={onAssumptions} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2">What the figures assume</button>.
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4"><ChevronLeft size={15} /> Change the sale or the new home</button>
                <button type="button" onClick={onNext} disabled={!ready} data-testid="dz-step2-next"
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-5 text-[14px] font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50">
                    See ways to use the money <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default DzStep2;
